import api from './client';

export interface Task {
  id: string;
  title: string;
  description?: string;
  type: 'BREEDING' | 'HEALTH_CHECK' | 'BANDING' | 'FEEDING' | 'MAINTENANCE' | 'GENERAL' |
        'SUPPLY_CHECK' | 'EQUIPMENT_MAINTENANCE' | 'HEALTH_TEST_FOLLOW_UP' | 'VACCINATION' |
        'DISEASE_MONITORING' | 'DOCUMENTATION' | 'FINANCIAL_REVIEW' | 'CUSTOMER_FOLLOW_UP' |
        'INVENTORY_COUNT' | 'CAGE_CLEANING' | 'ENVIRONMENTAL_CHECK' | 'QUARANTINE_REVIEW';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'OVERDUE' | 'DEFERRED' | 'ESCALATED';
  dueDate: string;
  completedDate?: string;
  assignedTo?: string;
  relatedEntityId?: string;
  relatedEntityType?: 'BIRD' | 'BREEDING_PAIR' | 'CLUTCH' | 'EGG' | 'SPECIES' | 'CAGE' | 'USER' |
                     'FEED_STOCK' | 'SUPPLY' | 'SUPPLIER' | 'CUSTOMER' | 'EQUIPMENT' | 'HEALTH_TEST' |
                     'DISEASE_RECORD' | 'DOCUMENT' | 'TRANSACTION' | 'ACCOUNT' | 'EXPENSE' | 'PURCHASE' |
                     'SALE' | 'NOTE' | 'PHOTO' | 'SYSTEM_SETTING';
  reminderSent: boolean;
  metadata?: {
    moduleType?: string;
    eventType?: string;
    notificationId?: string;
    estimatedDuration?: number;
    actualDuration?: number;
    requiresApproval?: boolean;
    approvedBy?: string;
    approvedAt?: string;
    notes?: string;
    attachments?: string[];
    tags?: string[];
    checklistItems?: {
      id: string;
      description: string;
      completed: boolean;
      completedAt?: string;
      completedBy?: string;
    }[];
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskDto {
  title: string;
  description?: string;
  type: Task['type'];
  priority: Task['priority'];
  dueDate: string;
  assignedTo?: string;
  relatedEntityId?: string;
  relatedEntityType?: Task['relatedEntityType'];
  metadata?: Task['metadata'];
}

export interface TaskFilters {
  status?: Task['status'];
  type?: Task['type'];
  priority?: Task['priority'];
  assignedTo?: string;
  entityType?: Task['relatedEntityType'];
  entityId?: string;
  moduleType?: string;
  overdue?: boolean;
  upcoming?: boolean;
  limit?: number;
  offset?: number;
}

export interface TaskStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  overdue: number;
  deferred: number;
  escalated: number;
  byType: Record<Task['type'], number>;
  byPriority: Record<Task['priority'], number>;
  byModule: Record<string, number>;
  completionRate: number;
  avgCompletionTime: number;
  overdueRate: number;
}

export const tasksApi = {
  // Get all tasks with enhanced filtering
  async getAll(filters?: TaskFilters): Promise<{ data: Task[] }> {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters?.entityType) params.append('entityType', filters.entityType);
    if (filters?.entityId) params.append('entityId', filters.entityId);
    if (filters?.moduleType) params.append('moduleType', filters.moduleType);
    if (filters?.overdue !== undefined) params.append('overdue', filters.overdue.toString());
    if (filters?.upcoming !== undefined) params.append('upcoming', filters.upcoming.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get<Task[]>(`tasks?${params.toString()}`);
    return { data: response.data };
  },

  // Get upcoming tasks
  async getUpcoming(): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>('tasks/upcoming');
    return { data: response.data };
  },

  // Get overdue tasks
  async getOverdue(): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>('tasks/overdue');
    return { data: response.data };
  },

  // Get tasks for specific entity
  async getByEntity(entityType: Task['relatedEntityType'], entityId: string): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>(`tasks/entity/${entityType}/${entityId}`);
    return { data: response.data };
  },

  // Get tasks by module
  async getByModule(moduleType: string): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>(`tasks/module/${moduleType}`);
    return { data: response.data };
  },

  // Get high priority tasks
  async getHighPriority(): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>('tasks/high-priority');
    return { data: response.data };
  },

  // Get critical tasks
  async getCritical(): Promise<{ data: Task[] }> {
    const response = await api.get<Task[]>('tasks/critical');
    return { data: response.data };
  },

  // Create new task
  async create(data: CreateTaskDto): Promise<{ data: Task }> {
    const response = await api.post<Task>('tasks', data);
    return { data: response.data };
  },

  // Create multiple tasks
  async createBatch(tasks: CreateTaskDto[]): Promise<{ data: Task[] }> {
    const response = await api.post<Task[]>('tasks/batch', { tasks });
    return { data: response.data };
  },

  // Update task
  async update(id: string, data: Partial<Task>): Promise<{ data: Task }> {
    const response = await api.put<Task>(`tasks/${id}`, data);
    return { data: response.data };
  },

  // Mark task as completed
  async markCompleted(id: string): Promise<{ data: Task }> {
    return this.update(id, { 
      status: 'COMPLETED',
      completedDate: new Date().toISOString()
    });
  },

  // Mark task as in progress
  async markInProgress(id: string): Promise<{ data: Task }> {
    return this.update(id, { status: 'IN_PROGRESS' });
  },

  // Defer task
  async defer(id: string, newDueDate: string, reason?: string): Promise<{ data: Task }> {
    return this.update(id, {
      status: 'DEFERRED',
      dueDate: newDueDate,
      metadata: { deferReason: reason }
    });
  },

  // Escalate task
  async escalate(id: string, reason?: string): Promise<{ data: Task }> {
    return this.update(id, {
      status: 'ESCALATED',
      metadata: { escalationReason: reason }
    });
  },

  // Reassign task
  async reassign(id: string, newAssigneeId: string): Promise<{ data: Task }> {
    return this.update(id, { assignedTo: newAssigneeId });
  },

  // Update checklist item
  async updateChecklistItem(taskId: string, itemId: string, completed: boolean): Promise<{ data: Task }> {
    const task = await this.getById(taskId);
    const checklistItems = task.metadata?.checklistItems || [];
    const updatedItems = checklistItems.map(item =>
      item.id === itemId
        ? { ...item, completed, completedAt: completed ? new Date().toISOString() : undefined }
        : item
    );

    return this.update(taskId, {
      metadata: {
        ...task.metadata,
        checklistItems: updatedItems
      }
    });
  },

  // Add checklist item
  async addChecklistItem(taskId: string, description: string): Promise<{ data: Task }> {
    const task = await this.getById(taskId);
    const checklistItems = task.metadata?.checklistItems || [];
    const newItem = {
      id: `checklist-${Date.now()}`,
      description,
      completed: false
    };

    return this.update(taskId, {
      metadata: {
        ...task.metadata,
        checklistItems: [...checklistItems, newItem]
      }
    });
  },

  // Get task by ID
  async getById(id: string): Promise<Task> {
    const response = await api.get<Task>(`tasks/${id}`);
    return response.data;
  },

  // Delete task
  async delete(id: string): Promise<void> {
    await api.delete(`tasks/${id}`);
  },

  // Get task statistics
  async getStats(): Promise<{ data: TaskStats }> {
    try {
      const response = await api.get<TaskStats>('tasks/stats');
      return { data: response.data };
    } catch (error) {
      // Fallback: calculate from all tasks if stats endpoint doesn't exist
      const allTasks = await this.getAll();
      const tasks = allTasks.data;

      const stats: TaskStats = {
        total: tasks.length,
        pending: tasks.filter(t => t.status === 'PENDING').length,
        inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
        completed: tasks.filter(t => t.status === 'COMPLETED').length,
        overdue: tasks.filter(t => t.status === 'OVERDUE').length,
        deferred: tasks.filter(t => t.status === 'DEFERRED').length,
        escalated: tasks.filter(t => t.status === 'ESCALATED').length,
        byType: tasks.reduce((acc, task) => {
          acc[task.type] = (acc[task.type] || 0) + 1;
          return acc;
        }, {} as Record<Task['type'], number>),
        byPriority: tasks.reduce((acc, task) => {
          acc[task.priority] = (acc[task.priority] || 0) + 1;
          return acc;
        }, {} as Record<Task['priority'], number>),
        byModule: tasks.reduce((acc, task) => {
          const module = task.metadata?.moduleType || 'general';
          acc[module] = (acc[module] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        completionRate: tasks.length > 0 ? (tasks.filter(t => t.status === 'COMPLETED').length / tasks.length) * 100 : 0,
        avgCompletionTime: 0,
        overdueRate: tasks.length > 0 ? (tasks.filter(t => t.status === 'OVERDUE').length / tasks.length) * 100 : 0
      };

      return { data: stats };
    }
  },

  // Bulk operations
  async bulkUpdate(ids: string[], updates: Partial<Task>): Promise<{ data: Task[] }> {
    const response = await api.put<Task[]>('tasks/bulk-update', { ids, updates });
    return { data: response.data };
  },

  async bulkDelete(ids: string[]): Promise<void> {
    await api.post('tasks/bulk-delete', { ids });
  },

  async bulkStatusUpdate(ids: string[], status: Task['status']): Promise<{ data: Task[] }> {
    const response = await api.put<Task[]>('tasks/bulk-status', { ids, status });
    return { data: response.data };
  },

  async bulkAssign(ids: string[], assigneeId: string): Promise<{ data: Task[] }> {
    const response = await api.put<Task[]>('tasks/bulk-assign', { ids, assigneeId });
    return { data: response.data };
  }
}; 